/**
 * Single source of truth for ABHA-status PRESENTATION (badge + CTA).
 *
 * Every surface — patient select list, appointment card, future patient profile — must consume
 * this instead of re-deriving switch statements, so the label/colour/CTA can never drift and any
 * new/unknown backend state degrades safely to "Add ABHA".
 *
 * Backend states (app/schemas/patient.py): NOT_LINKED | LINKED | VERIFIED | UNLINKED.
 * Ladder: NOT_LINKED → LINKED (number on file, ownership unproven) → VERIFIED (proven).
 * UNLINKED is reserved/unused; treated as NOT_LINKED.
 */
import { ADD_ABHA, VERIFY_ABHA } from '../../constants/abdmCopy';

// Solid tones (no faint alpha) so badges read in BOTH light and dark mode — per frontend CLAUDE.md.
export const ABHA_TONES = {
  green: { light: { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
           dark:  { bg: 'rgba(5,150,105,0.30)', border: 'rgba(52,211,153,0.45)', text: '#6ee7b7' } },
  blue:  { light: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
           dark:  { bg: 'rgba(37,99,235,0.30)', border: 'rgba(96,165,250,0.45)', text: '#93c5fd' } },
  amber: { light: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
           dark:  { bg: 'rgba(217,119,6,0.30)', border: 'rgba(251,191,36,0.45)', text: '#fcd34d' } },
};

/**
 * @param {string|undefined} status  the patient's abha_status
 * @returns {{
 *   key: string, label: string, icon: string, tone: 'green'|'blue'|'amber',
 *   cta: string|null, ctaIntent: 'create'|'verify'|null, needsAction: boolean
 * }}
 * `ctaIntent` maps 1:1 to ABHASection's `initialMode` ('create' | 'verify').
 */
export function abhaStatusMeta(status) {
  if (status === 'VERIFIED') {
    return { key: 'VERIFIED', label: 'ABHA verified', icon: '✓', tone: 'green',
             cta: null, ctaIntent: null, needsAction: false };
  }
  if (status === 'LINKED') {
    return { key: 'LINKED', label: 'ABHA not verified', icon: '', tone: 'blue',
             cta: VERIFY_ABHA, ctaIntent: 'verify', needsAction: true };
  }
  // NOT_LINKED, UNLINKED, undefined, or any future/unknown state → safe default.
  // ctaIntent is null → ABHASection opens its CHOOSER (create-new / verify-existing / scan),
  // NOT forced Create: "no ABHA in our DB" does not mean the patient has no ABHA — most already
  // have one, and force-creating would mint a duplicate. The receptionist links or creates.
  return { key: 'NONE', label: 'No ABHA', icon: '', tone: 'amber',
           cta: ADD_ABHA, ctaIntent: null, needsAction: true };
}

/** Resolve solid badge colours for a meta + theme. */
export function abhaToneColors(tone, isDark) {
  const t = ABHA_TONES[tone] || ABHA_TONES.amber;
  return isDark ? t.dark : t.light;
}
